import { AvisoErrorExportacion } from '@/components/dominio/AvisoErrorExportacion';
import {
  type PropsExcluidas,
  SelectorObrasExcluidas,
} from '@/components/dominio/SelectorObrasExcluidas';
import { TituloOrdenable } from '@/components/dominio/TituloOrdenable';
import { MultiSelectPopup } from '@/components/filtros/MultiSelectPopup';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useViviendaCostos } from '@/hooks/useAvanceMatriz';
import { useExportacion } from '@/hooks/useExportacion';
import { useFiltrosGlobales } from '@/lib/FiltrosGlobalesContext';
import type { FilaTabla, ReporteTablaExport } from '@/lib/exportarViviendaGerencia';
import {
  describirExcluidas,
  describirPeriodo,
  fmtMonto,
  fmtPct,
  razon,
} from '@/lib/formatoGerencia';
import { type OrdenColumna, ordenarHermanos, siguienteOrden } from '@/lib/ordenColumna';
import type { ViviendaCosto } from '@adelante/shared';
import { CaretDown, CaretRight, CircleNotch, DownloadSimple, FilePdf } from '@phosphor-icons/react';
import { type ReactElement, useMemo, useState } from 'react';

/** Acumulado de un nodo: costo directo y la parte de mano de obra. */
interface Suma {
  costo: number;
  mo: number;
}
const cero = (): Suma => ({ costo: 0, mo: 0 });
const sumar = (a: Suma, b: Suma) => {
  a.costo += b.costo;
  a.mo += b.mo;
};
/** Materiales y maquinaria = costo directo − mano de obra (igual que Costos/m²). */
const matMaq = (s: Suma) => s.costo - s.mo;

interface Partida {
  taskNo: string;
  etiqueta: string;
  suma: Suma;
}
interface Tarea {
  padre: string;
  etiqueta: string;
  suma: Suma;
  partidas: Partida[];
}
interface Obra {
  worksNo: string;
  display: string | null;
  suma: Suma;
  tareas: Tarea[];
}
interface Bloque {
  bloque: string;
  suma: Suma;
  obras: Obra[];
}
interface Proyecto {
  proyecto: string;
  suma: Suma;
  bloques: Bloque[];
}

const natural = (a: string, b: string) => a.localeCompare(b, 'es', { numeric: true });

function construirArbol(filas: ViviendaCosto[]) {
  const proyectos = new Map<string, Proyecto>();
  const bloques = new Map<string, Bloque>();
  const obras = new Map<string, Obra>();
  const tareas = new Map<string, Tarea>();
  const total = cero();

  for (const f of filas) {
    const s: Suma = { costo: f.costo, mo: f.costoMO };
    const proy = f.bloque ?? '(sin proyecto)';
    const blq = f.subBloque ?? '(sin bloque)';
    let nP = proyectos.get(proy);
    if (!nP) {
      nP = { proyecto: proy, suma: cero(), bloques: [] };
      proyectos.set(proy, nP);
    }
    const bKey = `${proy}|${blq}`;
    let nB = bloques.get(bKey);
    if (!nB) {
      nB = { bloque: blq, suma: cero(), obras: [] };
      bloques.set(bKey, nB);
      nP.bloques.push(nB);
    }
    let nO = obras.get(f.worksNo);
    if (!nO) {
      nO = { worksNo: f.worksNo, display: f.displayName, suma: cero(), tareas: [] };
      obras.set(f.worksNo, nO);
      nB.obras.push(nO);
    }
    const tKey = `${f.worksNo}|${f.tareaPadre}`;
    let nT = tareas.get(tKey);
    if (!nT) {
      nT = {
        padre: f.tareaPadre,
        etiqueta: f.tareaPadreDisplay ?? f.tareaPadre,
        suma: cero(),
        partidas: [],
      };
      tareas.set(tKey, nT);
      nO.tareas.push(nT);
    }
    nT.partidas.push({ taskNo: f.taskNo, etiqueta: f.tareaDisplay ?? f.taskNo, suma: s });
    for (const nodo of [nT, nO, nB, nP]) sumar(nodo.suma, s);
    sumar(total, s);
  }

  const lista = [...proyectos.values()].sort((a, b) => natural(a.proyecto, b.proyecto));
  for (const p of lista) {
    p.bloques.sort((a, b) => natural(a.bloque, b.bloque));
    for (const b of p.bloques) {
      b.obras.sort((a, c) => natural(a.worksNo, c.worksNo));
      for (const o of b.obras) {
        o.tareas.sort((a, c) => natural(a.padre, c.padre));
        for (const t of o.tareas) t.partidas.sort((a, c) => natural(a.taskNo, c.taskNo));
      }
    }
  }
  return { proyectos: lista, total };
}

/** Base de las razones: el total de la propia fila, o el costo total de la obra. */
type BaseRelacion = 'fila' | 'obra';

const columnasDe = (base: BaseRelacion): [string, string, string, string, string] => [
  'Costo total',
  'Materiales y maquinaria',
  'Mano de obra',
  base === 'obra' ? 'Mat. y maq. ÷ Costo de la obra' : 'Mat. y maq. ÷ Costo total',
  base === 'obra' ? 'M.O. ÷ Costo de la obra' : 'M.O. ÷ Costo total',
];

export function ViviendaCostosPartida({ excluidas, onExcluidas }: PropsExcluidas) {
  const { data, isLoading, error } = useViviendaCostos();
  // Obras que el usuario decidió excluir (se descartan antes de cualquier cálculo).
  const filasObras = useMemo(
    () => (data?.data ?? []).filter((f) => !excluidas.has(f.worksNo)),
    [data, excluidas],
  );
  const { filtros } = useFiltrosGlobales();
  const [expandidos, setExpandidos] = useState<Set<string>>(new Set());
  const [orden, setOrden] = useState<OrdenColumna | null>(null);
  const { exportando, error: errorExport, ejecutar, limpiarError } = useExportacion();

  // Filtro local de partida (jerárquico Tarea Padre → Partida). Vacío = todas.
  const [padresSel, setPadresSel] = useState<Set<string>>(new Set());
  const [partidasSel, setPartidasSel] = useState<Set<string>>(new Set());

  // Catálogo de partidas derivado de los propios datos (son las mismas en todas las casas).
  const catalogo = useMemo(() => {
    const padres = new Map<string, string>();
    const partidas = new Map<string, { padre: string; label: string }>();
    for (const f of filasObras) {
      if (!padres.has(f.tareaPadre)) padres.set(f.tareaPadre, f.tareaPadreDisplay ?? f.tareaPadre);
      if (!partidas.has(f.taskNo)) {
        partidas.set(f.taskNo, { padre: f.tareaPadre, label: f.tareaDisplay ?? f.taskNo });
      }
    }
    return { padres, partidas };
  }, [filasObras]);

  const padresOpts = useMemo(
    () =>
      [...catalogo.padres]
        .sort((a, b) => natural(a[0], b[0]))
        .map(([value, label]) => ({ value, label })),
    [catalogo],
  );
  // Partidas de las tareas padre seleccionadas (para el segundo selector).
  const partidasOpts = useMemo(
    () =>
      [...catalogo.partidas]
        .filter(([, v]) => padresSel.has(v.padre))
        .sort((a, b) => natural(a[0], b[0]))
        .map(([value, v]) => ({ value, label: v.label })),
    [catalogo, padresSel],
  );
  // Partidas marcadas que aún pertenecen a un padre seleccionado.
  const partidasEfectivas = useMemo(
    () =>
      new Set([...partidasSel].filter((t) => padresSel.has(catalogo.partidas.get(t)?.padre ?? ''))),
    [partidasSel, padresSel, catalogo],
  );
  const filtroPartidaActivo = padresSel.size > 0 || partidasEfectivas.size > 0;

  const filasFiltradas = useMemo(
    () =>
      filasObras.filter(
        (f) =>
          (padresSel.size === 0 || padresSel.has(f.tareaPadre)) &&
          (partidasEfectivas.size === 0 || partidasEfectivas.has(f.taskNo)),
      ),
    [filasObras, padresSel, partidasEfectivas],
  );

  const arbol = useMemo(() => construirArbol(filasFiltradas), [filasFiltradas]);

  // Base de las razones. 'obra' = contra el costo TOTAL de la obra (con todas sus
  // partidas, aunque haya filtro de partida): sirve para ver el peso de una partida
  // dentro de la casa. En proyecto/bloque la base es la suma de sus obras completas.
  const [base, setBase] = useState<BaseRelacion>('fila');
  const COLUMNAS = columnasDe(base);
  const bases = useMemo(() => {
    const completo = construirArbol(filasObras);
    const proy = new Map<string, number>();
    const blq = new Map<string, number>();
    const obra = new Map<string, number>();
    for (const p of completo.proyectos) {
      proy.set(p.proyecto, p.suma.costo);
      for (const b of p.bloques) {
        blq.set(`${p.proyecto}|${b.bloque}`, b.suma.costo);
        for (const o of b.obras) obra.set(o.worksNo, o.suma.costo);
      }
    }
    return { proy, blq, obra, total: completo.total.costo };
  }, [filasObras]);
  /** Denominador de las razones de una fila: su base de obra, o su propio costo. */
  const denDe = (s: Suma, baseCosto?: number) =>
    base === 'obra' && baseCosto ? baseCosto : s.costo;

  const togglePadre = (v: string) =>
    setPadresSel((prev) => {
      const next = new Set(prev);
      if (next.has(v)) next.delete(v);
      else next.add(v);
      return next;
    });
  const togglePartida = (v: string) =>
    setPartidasSel((prev) => {
      const next = new Set(prev);
      if (next.has(v)) next.delete(v);
      else next.add(v);
      return next;
    });
  const limpiarPartida = () => {
    setPadresSel(new Set());
    setPartidasSel(new Set());
  };

  const toggle = (key: string) =>
    setExpandidos((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const todasKeys = useMemo(() => {
    const k = new Set<string>();
    for (const p of arbol.proyectos) {
      k.add(`P|${p.proyecto}`);
      for (const b of p.bloques) {
        k.add(`B|${p.proyecto}|${b.bloque}`);
        for (const o of b.obras) k.add(`O|${o.worksNo}`);
      }
    }
    return k;
  }, [arbol]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <CircleNotch className="h-4 w-4 animate-spin" weight="bold" /> Cargando…
      </div>
    );
  }
  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="pt-6">
          <p className="text-destructive">Error al cargar: {error.message}</p>
        </CardContent>
      </Card>
    );
  }

  const valores = (s: Suma, baseCosto?: number): (number | null)[] => [
    s.costo || null,
    matMaq(s) || null,
    s.mo || null,
    razon(matMaq(s), denDe(s, baseCosto)),
    razon(s.mo, denDe(s, baseCosto)),
  ];

  const celdas = (s: Suma, baseCosto?: number) => [
    <TableCell key="c" className="text-right text-xs tabular-nums">
      {fmtMonto(s.costo)}
    </TableCell>,
    <TableCell key="m" className="text-right text-xs tabular-nums">
      {fmtMonto(matMaq(s))}
    </TableCell>,
    <TableCell key="o" className="text-right text-xs tabular-nums">
      {fmtMonto(s.mo)}
    </TableCell>,
    <TableCell key="pm" className="bg-emerald-50/60 text-right text-xs font-semibold tabular-nums">
      {fmtPct(matMaq(s), denDe(s, baseCosto))}
    </TableCell>,
    <TableCell key="po" className="bg-emerald-50/60 text-right text-xs font-semibold tabular-nums">
      {fmtPct(s.mo, denDe(s, baseCosto))}
    </TableCell>,
  ];

  const caret = (abierto: boolean) =>
    abierto ? (
      <CaretDown className="h-3 w-3" weight="bold" />
    ) : (
      <CaretRight className="h-3 w-3" weight="bold" />
    );

  // ---- Filas de la tabla (árbol) ----
  /** Hermanos de un nivel en el orden elegido; `baseDe` da la base de las razones de cada fila. */
  const hermanos = <T extends { suma: Suma }>(items: T[], baseDe?: (n: T) => number | undefined) =>
    ordenarHermanos(items, orden, (n) => valores(n.suma, baseDe?.(n)));
  const alOrdenar = (idx: number) => setOrden((actual) => siguienteOrden(actual, idx));

  const filas: ReactElement[] = [];
  for (const p of hermanos(arbol.proyectos, (n) => bases.proy.get(n.proyecto))) {
    const pKey = `P|${p.proyecto}`;
    const pOpen = expandidos.has(pKey);
    filas.push(
      <TableRow key={pKey} className="bg-emerald-100 font-semibold">
        <TableCell className="sticky left-0 z-10 bg-emerald-100 text-xs">
          <button
            type="button"
            onClick={() => toggle(pKey)}
            className="flex items-center gap-1 hover:underline"
          >
            {caret(pOpen)}
            {p.proyecto}
          </button>
        </TableCell>
        {celdas(p.suma, bases.proy.get(p.proyecto))}
      </TableRow>,
    );
    if (!pOpen) continue;
    for (const b of hermanos(p.bloques, (n) => bases.blq.get(`${p.proyecto}|${n.bloque}`))) {
      const bKey = `B|${p.proyecto}|${b.bloque}`;
      const bOpen = expandidos.has(bKey);
      filas.push(
        <TableRow key={bKey} className="bg-emerald-50 font-medium">
          <TableCell className="sticky left-0 z-10 bg-emerald-50 text-xs">
            <button
              type="button"
              onClick={() => toggle(bKey)}
              className="flex items-center gap-1 pl-4 hover:underline"
            >
              {caret(bOpen)}
              {b.bloque}
            </button>
          </TableCell>
          {celdas(b.suma, bases.blq.get(`${p.proyecto}|${b.bloque}`))}
        </TableRow>,
      );
      if (!bOpen) continue;
      for (const o of hermanos(b.obras, (n) => bases.obra.get(n.worksNo))) {
        const oKey = `O|${o.worksNo}`;
        const oOpen = expandidos.has(oKey);
        filas.push(
          <TableRow key={oKey}>
            <TableCell className="sticky left-0 z-10 bg-background text-xs">
              <button
                type="button"
                onClick={() => toggle(oKey)}
                className="flex items-center gap-1 pl-9 text-left hover:underline"
              >
                {caret(oOpen)}
                <span>
                  <span className="font-medium">{o.worksNo}</span>
                  {o.display && o.display !== o.worksNo && (
                    <span className="ml-1 text-[10px] text-muted-foreground">· {o.display}</span>
                  )}
                </span>
              </button>
            </TableCell>
            {celdas(o.suma, bases.obra.get(o.worksNo))}
          </TableRow>,
        );
        if (!oOpen) continue;
        for (const t of hermanos(o.tareas, () => bases.obra.get(o.worksNo))) {
          filas.push(
            <TableRow key={`T|${o.worksNo}|${t.padre}`} className="bg-zinc-50 font-medium">
              <TableCell className="sticky left-0 z-10 bg-zinc-50 text-xs">
                <div className="pl-[3.5rem]">{t.etiqueta}</div>
              </TableCell>
              {celdas(t.suma, bases.obra.get(o.worksNo))}
            </TableRow>,
          );
          for (const pa of hermanos(t.partidas, () => bases.obra.get(o.worksNo))) {
            filas.push(
              <TableRow key={`A|${o.worksNo}|${pa.taskNo}`}>
                <TableCell className="sticky left-0 z-10 bg-background text-xs text-zinc-600">
                  <div className="pl-[4.75rem]">{pa.etiqueta}</div>
                </TableCell>
                {celdas(pa.suma, bases.obra.get(o.worksNo))}
              </TableRow>,
            );
          }
        }
      }
    }
  }

  // ---- Exportación (Excel completo con partidas; PDF hasta nivel Obra) ----
  const armarReporte = (): ReporteTablaExport => {
    const hoy = new Date().toLocaleDateString('es-CR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    const filtrosActivos = [
      filtros.filtroProyecto && `Proyecto: ${filtros.filtroProyecto}`,
      filtros.filtroBloque && `Bloque: ${filtros.filtroBloque}`,
      filtros.filtroObra && `Obra: ${filtros.filtroObra}`,
      filtros.filtroModelo && `Modelo: ${filtros.filtroModelo}`,
      filtros.estadosSel?.length && `Estado de venta actual: ${filtros.estadosSel.join(', ')}`,
      describirExcluidas(excluidas, data?.data ?? []),
    ].filter(Boolean);
    const t = arbol.total;
    const descPartida = filtroPartidaActivo
      ? partidasEfectivas.size > 0
        ? [...partidasEfectivas]
            .sort(natural)
            .map((x) => catalogo.partidas.get(x)?.label ?? x)
            .join(', ')
        : [...padresSel]
            .sort(natural)
            .map((x) => catalogo.padres.get(x) ?? x)
            .join(', ')
      : null;

    const filasExp: FilaTabla[] = [];
    for (const p of hermanos(arbol.proyectos, (n) => bases.proy.get(n.proyecto))) {
      filasExp.push({
        nivel: 0,
        etiqueta: p.proyecto,
        valores: valores(p.suma, bases.proy.get(p.proyecto)),
      });
      for (const b of hermanos(p.bloques, (n) => bases.blq.get(`${p.proyecto}|${n.bloque}`))) {
        filasExp.push({
          nivel: 1,
          etiqueta: b.bloque,
          valores: valores(b.suma, bases.blq.get(`${p.proyecto}|${b.bloque}`)),
        });
        for (const o of hermanos(b.obras, (n) => bases.obra.get(n.worksNo))) {
          filasExp.push({
            nivel: 2,
            etiqueta: o.worksNo,
            sublabel: o.display && o.display !== o.worksNo ? o.display : null,
            valores: valores(o.suma, bases.obra.get(o.worksNo)),
          });
          for (const ta of hermanos(o.tareas, () => bases.obra.get(o.worksNo))) {
            filasExp.push({
              nivel: 3,
              etiqueta: ta.etiqueta,
              valores: valores(ta.suma, bases.obra.get(o.worksNo)),
            });
            for (const pa of hermanos(ta.partidas, () => bases.obra.get(o.worksNo))) {
              filasExp.push({
                nivel: 4,
                etiqueta: pa.etiqueta,
                valores: valores(pa.suma, bases.obra.get(o.worksNo)),
              });
            }
          }
        }
      }
    }
    filasExp.push({
      nivel: 0,
      total: true,
      etiqueta: 'Total viviendas',
      valores: valores(t, bases.total),
    });

    return {
      titulo: 'Vivienda Gerencia · Costo por obra y partida',
      subtitulo: [
        'Viviendas VB, VC y VN (área PRO VIVIENDA)',
        `Período: ${describirPeriodo(filtros)} · Generado el ${hoy}${filtrosActivos.length ? ` · ${filtrosActivos.join(' · ')}` : ''}${descPartida ? ` · Partida: ${descPartida}` : ''}${base === 'obra' ? ' · Relaciones sobre el costo total de la obra' : ''}`,
        'Materiales y maquinaria = costo directo menos mano de obra. Costo directo: sin cuenta SSCC ni partidas indirectas (CI).',
      ],
      kpis: [
        { titulo: 'Costo total', valor: fmtMonto(t.costo) },
        { titulo: 'Materiales y maquinaria', valor: fmtMonto(matMaq(t)) },
        { titulo: 'Mano de obra', valor: fmtMonto(t.mo) },
        { titulo: 'Mat. y maq. ÷ Costo', valor: fmtPct(matMaq(t), denDe(t, bases.total)) },
        { titulo: 'M.O. ÷ Costo', valor: fmtPct(t.mo, denDe(t, bases.total)) },
      ],
      tabla: {
        titulo: 'Costo por partida',
        arbol: true,
        nivelMaxPdf: 2,
        encabezado: 'Proyecto / Bloque / Obra / Partida',
        columnas: [
          { titulo: COLUMNAS[0], tipo: 'monto' },
          { titulo: COLUMNAS[1], tipo: 'monto' },
          { titulo: COLUMNAS[2], tipo: 'monto' },
          { titulo: COLUMNAS[3], tipo: 'pct' },
          { titulo: COLUMNAS[4], tipo: 'pct' },
        ],
        filas: filasExp,
      },
      nombreArchivo: `vivienda-costo-partida-${new Date().toISOString().slice(0, 10)}`,
    };
  };

  const exportar = (formato: 'xlsx' | 'pdf') =>
    ejecutar(formato, async () => {
      const rep = armarReporte();
      const lib = await import('@/lib/exportarViviendaGerencia');
      if (formato === 'xlsx') await lib.exportarTablaExcel(rep);
      else await lib.exportarTablaPdf(rep);
    });

  const t = arbol.total;
  const tarjetas = [
    { titulo: 'Costo total', valor: fmtMonto(t.costo), sub: 'Costo directo de las viviendas' },
    {
      titulo: 'Materiales y maquinaria',
      valor: fmtMonto(matMaq(t)),
      sub: 'Costo total − mano de obra',
    },
    { titulo: 'Mano de obra', valor: fmtMonto(t.mo), sub: 'Cuenta 6010060000000' },
    {
      titulo: COLUMNAS[3],
      valor: fmtPct(matMaq(t), denDe(t, bases.total)),
      sub: 'Peso de materiales y maquinaria',
      destacada: true,
    },
    {
      titulo: COLUMNAS[4],
      valor: fmtPct(t.mo, denDe(t, bases.total)),
      sub: 'Peso de la mano de obra',
      destacada: true,
    },
  ];

  const botonExport =
    'inline-flex items-center gap-1 rounded-md border border-emerald-700 bg-white px-2 py-1 font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-50';

  return (
    <div className="space-y-4">
      {errorExport && <AvisoErrorExportacion error={errorExport} onCerrar={limpiarError} />}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {tarjetas.map((c) => (
          <Card key={c.titulo} className={c.destacada ? 'border-emerald-300 bg-emerald-50/60' : ''}>
            <CardContent className="p-4">
              <div className="text-xs font-medium text-muted-foreground">{c.titulo}</div>
              <div className="mt-1 text-xl font-semibold tabular-nums">{c.valor}</div>
              <div className="mt-0.5 text-[11px] text-muted-foreground">{c.sub}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="flex flex-wrap items-center gap-3 border-b px-4 py-2 text-xs">
            <span className="text-sm font-semibold">Costo por obra y partida</span>
            <span className="text-zinc-300">|</span>
            <button
              type="button"
              onClick={() => setExpandidos(new Set(todasKeys))}
              className="text-blue-600 hover:underline"
            >
              Expandir hasta obras
            </button>
            <span className="text-zinc-300">|</span>
            <button
              type="button"
              onClick={() => setExpandidos(new Set())}
              className="text-blue-600 hover:underline"
            >
              Colapsar
            </button>
            <span className="text-zinc-300">|</span>
            <SelectorObrasExcluidas
              filas={data?.data ?? []}
              excluidas={excluidas}
              onChange={onExcluidas}
            />
            <span className="text-zinc-300">|</span>
            <span className="text-muted-foreground">Partida:</span>
            <MultiSelectPopup
              titulo="Tarea padre"
              opciones={padresOpts}
              seleccion={padresSel}
              onToggle={togglePadre}
              onNinguna={limpiarPartida}
            />
            <MultiSelectPopup
              titulo="Partida"
              opciones={partidasOpts}
              seleccion={partidasEfectivas}
              onToggle={togglePartida}
              onTodas={() => setPartidasSel(new Set(partidasOpts.map((o) => o.value)))}
              onNinguna={() => setPartidasSel(new Set())}
              disabled={padresSel.size === 0}
              ancho="w-80"
            />
            {filtroPartidaActivo && (
              <button
                type="button"
                onClick={limpiarPartida}
                className="text-blue-600 hover:underline"
              >
                Limpiar partida
              </button>
            )}
            <span className="text-zinc-300">|</span>
            <span className="text-muted-foreground">Relación sobre:</span>
            <span className="inline-flex overflow-hidden rounded-md border border-zinc-300">
              {(
                [
                  ['fila', 'Total de la fila'],
                  ['obra', 'Total de la obra'],
                ] as const
              ).map(([v, texto]) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setBase(v)}
                  className={`px-2.5 py-1 ${
                    base === v
                      ? 'bg-emerald-700 text-white'
                      : 'bg-white text-zinc-600 hover:bg-zinc-100'
                  }`}
                >
                  {texto}
                </button>
              ))}
            </span>
            <span className="ml-auto flex items-center gap-2">
              <button
                type="button"
                onClick={() => exportar('xlsx')}
                disabled={exportando !== null}
                className={botonExport}
              >
                <DownloadSimple className="h-3.5 w-3.5" weight="bold" />
                {exportando === 'xlsx' ? 'Generando…' : 'Exportar a Excel'}
              </button>
              <button
                type="button"
                onClick={() => exportar('pdf')}
                disabled={exportando !== null}
                className={botonExport}
              >
                <FilePdf className="h-3.5 w-3.5" weight="bold" />
                {exportando === 'pdf' ? 'Generando…' : 'Exportar a PDF'}
              </button>
            </span>
          </div>
          <Table wrapperClassName="max-h-[calc(100vh-300px)]">
            <TableHeader>
              <TableRow>
                <TableHead className="sticky left-0 top-0 z-30 min-w-[300px] bg-background">
                  Proyecto / Bloque / Obra / Partida
                </TableHead>
                {COLUMNAS.map((c, i) => (
                  <TableHead
                    key={c}
                    className="sticky top-0 z-20 min-w-[130px] bg-background text-right text-xs"
                  >
                    <TituloOrdenable titulo={c} idx={i} orden={orden} onOrdenar={alOrdenar} />
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filas}
              <TableRow className="border-t-2 border-zinc-300 bg-zinc-100 font-bold">
                <TableCell className="sticky left-0 z-10 bg-zinc-100 text-xs">
                  Total viviendas
                </TableCell>
                {celdas(t, bases.total)}
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <p className="text-[11px] text-muted-foreground">
        Costo total: costo directo de obra, sin la cuenta SSCC ni partidas indirectas (CI). Mano de
        obra: cuenta 6010060000000. Materiales y maquinaria: costo total menos mano de obra. Las dos
        razones suman 100 % cuando se miden sobre el total de la fila; con «Total de la obra» cada
        partida se compara contra el costo completo de su obra (aunque haya filtro de partida), así
        que muestra su peso dentro de la casa. El filtro de partida recalcula tarjetas, totales y
        exportaciones. El PDF incluye hasta el nivel de obra; el Excel incluye todas las partidas.
        El filtro de Estado de venta excluye obras según su estado actual y trae todo su costo de
        principio a fin.
      </p>
    </div>
  );
}
